import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { PropertyTable } from '../metadata/propertyConfig';
import { Variants } from '@/types/common';
import { showAlert } from '../../../../slice/AlertSlice';
import { useSendNotificationMutation } from '../../services/propertyApi';

export interface UseEditCancelModalReturn {
  showModel: boolean;
  headerText: string;
  subHeaderText: string;
  placeHolderText: string;
  orderId: string;
  formattedAddress: string;
  tableAction: string;
  changeNotes: string | undefined;
  setChangeNotes: React.Dispatch<React.SetStateAction<string | undefined>>;
  isValid: boolean;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
  isLoader: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  handleModalClose: () => void;
  handleEditOrderRequest: () => void;
  handleCancelOrderRequest: () => void;
  openEditCancelModal: (action: string, rowData: PropertyTable) => void;
}

export function useEditCancelModal(): UseEditCancelModalReturn {
  const dispatch = useDispatch();
  const [sendNotification] = useSendNotificationMutation();
  const [showModel, setShowModel] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [tableAction, setTableAction] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [subHeaderText, setSubHeaderText] = useState('');
  const [placeHolderText, setPlaceHolderText] = useState('');
  const [orderId, setOrderId] = useState('');
  const [formattedAddress, setAddress] = useState('');
  const [tableDataId, setTableDataId] = useState('');
  const [changeNotes, setChangeNotes] = useState<string | undefined>();
  const [isLoader, setIsLoader] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      if (typeof changeNotes === 'string') {
        inputRef.current?.setSelectionRange(
          changeNotes.length,
          changeNotes.length
        );
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [changeNotes]);

  const editCancelRequestOrderApi = async (
    requestBody: Record<string, string>
  ) => {
    try {
      await sendNotification(requestBody).unwrap();
      setShowModel(false);
      setIsLoader(false);
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Request has been sent to operations team'
        })
      );
    } catch (error: unknown) {
      setIsLoader(false);
      setShowModel(false);
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    }
  };

  const handleEditOrderRequest = () => {
    if (!changeNotes || changeNotes === '') {
      setIsValid(true);
      return;
    }
    setIsValid(false);
    setIsLoader(true);
    editCancelRequestOrderApi({
      action: 'Update Order',
      orderId: tableDataId,
      subject: 'Request for Update Order',
      content: changeNotes,
      propertyAddress: formattedAddress
    });
    setChangeNotes('');
  };

  const handleCancelOrderRequest = () => {
    if (!changeNotes || changeNotes === '') {
      setIsValid(true);
      return;
    }
    setIsValid(false);
    setIsLoader(true);
    editCancelRequestOrderApi({
      action: 'Cancel Order',
      orderId: tableDataId,
      subject: 'Request for Cancel Order',
      content: changeNotes,
      propertyAddress: formattedAddress
    });
    setChangeNotes('');
  };

  const handleModalClose = () => {
    setChangeNotes('');
    setIsValid(false);
    setShowModel(false);
  };

  const openEditCancelModal = (action: string, rowData: PropertyTable) => {
    setTableAction(action);
    setPlaceHolderText(
      action === 'Edit Order'
        ? 'e.g. I need to change the lockbox access code'
        : 'e.g. I just leased the property!'
    );
    setHeaderText(
      action === 'Edit Order'
        ? 'Request to Edit Order'
        : 'Request to Cancel Order'
    );
    setSubHeaderText(
      action === 'Edit Order'
        ? 'Need to make a change to your order? Please provide a brief summary and our team will respond shortly to confirm the request.'
        : 'Need to cancel your order? Please provide a brief reason for cancellation and our team will respond shortly to confirm the request.'
    );
    // orderNumber.data carries the raw order number; orderNumber.component
    // is the rendered cell. Read the data, then format for display.
    const rawOrderNumber = rowData?.orderNumber?.data ?? '';
    setOrderId(rawOrderNumber ? ` #${rawOrderNumber}` : '');
    setAddress(rowData?.formattedAddress!);
    setTableDataId(rowData?.id);
    setShowModel(true);
  };

  return {
    showModel,
    headerText,
    subHeaderText,
    placeHolderText,
    orderId,
    formattedAddress,
    tableAction,
    changeNotes,
    setChangeNotes,
    isValid,
    setIsValid,
    isLoader,
    inputRef,
    handleModalClose,
    handleEditOrderRequest,
    handleCancelOrderRequest,
    openEditCancelModal
  };
}
